/**
 * Finding a line by typing what you would say out loud.
 *
 * A planner looking for a cable says "CYYF 3x25+16" or just "3x2,5", and asks
 * for a customer by name. None of that matches the stored text: the spec is
 * rendered "3×2.5" with a multiplication sign, the earth core is written
 * "+ GY 16", and half the keyboards in the plant put a comma in a decimal.
 * Typing the obvious thing found nothing, which reads as "we don't have it".
 *
 * So both sides are flattened to the same shape before they meet, and the row
 * carries every spelling it could reasonably be called by.
 *
 * Terms are AND-ed: "test 3x2.5" means that customer AND that cable, which is
 * the question actually being asked when both are typed.
 */

/** One shape for both sides: no ×, no commas, no double spaces, lower case. */
export function normalise(s) {
  return String(s ?? '')
    .toLowerCase()
    .replace(/×/g, 'x')
    .replace(/,/g, '.')
    .replace(/\s+/g, ' ')
    .trim();
}

/** "3x25+16" — the way it is said, not the way it is stored. */
export function specForms(product) {
  const cores = Number(product?.numberOfConductors) || 0;
  const section = Number(product?.section);
  if (!cores || !Number.isFinite(section) || section <= 0) return [];

  const trim = n => String(Number(Number(n).toFixed(3)));
  const sec = trim(section);

  const gyRaw = product?.hasMixedConductorSections
    ? Number(product?.greenYellowSection)
    : null;
  const gy = Number.isFinite(gyRaw) && gyRaw > 0 ? trim(gyRaw) : null;

  const base = `${cores}x${sec}`;
  const forms = [base, `${cores} x ${sec}`];

  if (gy) {
    forms.push(
      `${base}+${gy}`,
      `${base} + ${gy}`,
      `${cores} x ${sec} + ${gy}`,
      // the way the app itself renders it, so pasting from the screen works
      `${base} + gy ${gy}`,
      `gy ${gy}`
    );
  }
  return forms;
}

/**
 * Everything a row could reasonably be searched by, in one blob.
 *
 * @param row  { client, orderCode, cableName, color, product }
 */
export function cableSearchKey(row) {
  const product = row?.product || row;
  const name = String(product?.cableName || row?.cableName || '');
  const specs = specForms(product);

  const parts = [
    row?.client,
    row?.orderCode,
    name,
    product?.color || row?.color,
    ...specs,
    // the full name as it is spoken: "CYYF 3x25+16"
    ...specs.map(s => `${name} ${s}`)
  ];

  const key = normalise(parts.filter(Boolean).join(' '));
  // A spaceless copy, so "3x25+16" finds a row that spells it "3 x 25 + 16".
  return `${key} ${key.replace(/ /g, '')}`;
}

/** Every term must be somewhere in the key. */
export function matchesSearch(key, query) {
  const q = normalise(query);
  if (!q) return true;

  const hay = String(key || '');
  return q
    .split(' ')
    .filter(Boolean)
    .every(term => hay.includes(term) || hay.includes(term.replace(/ /g, '')));
}

/**
 * Everything a launched JOB could reasonably be searched by.
 *
 * The Projects table takes raw jobs, not the order-line shape `cableSearchKey`
 * was written for, and tanstack's default global filter tests each COLUMN value
 * for the whole query. The columns are the job id, `name`, the planned length
 * and the status — so "CYYF" matched (it is `name`), "5x35" matched nothing at
 * all, and "CYYF 5x35" matched nothing either, because no single column holds
 * both halves. Three quarters of the obvious searches came back empty.
 *
 * The three shapes a planner types, all of which must work:
 *   · "CYYF" / "FY"        → the cable family
 *   · "5x35" / "3x25+16"   → cores × section, earth core included
 *   · "CYYF 3x25+16"       → the whole product, as the screen prints it
 *
 * Spec comes off the job's own fields with the launch productKey as a fallback,
 * so a factory-synced job with half its identity missing is still findable.
 * `productLabel` goes in verbatim as well — it is what the row actually shows,
 * which makes pasting from the screen work even for a shape `specForms` does not
 * generate.
 *
 * The job id goes in as its displayed 8-character prefix. Hex is only [0-9a-f],
 * and every cable family carries a letter outside that range, so a name search
 * can never be dragged into an id match.
 *
 * Takes a RAW job or an `adaptJobToProjectRow` DTO — the three views sharing the
 * projects search box feed it both, and the DTO drops the mixed-GY flag, the
 * launch key, and turns `status` into a `{ label }` object.
 */
export function jobSearchKey(job) {
  const fromKey = productFromKey(job?.source?.launch?.productKey);

  const section = job?.section ?? fromKey?.section;
  const gy = job?.greenYellowSection ?? fromKey?.greenYellowSection ?? null;
  const mixedRaw =
    job?.hasMixedConductorSections ?? fromKey?.hasMixedConductorSections;

  // The flag, when there is one. When there is not — the project DTO drops it —
  // a positive earth section that differs from the main one IS the reduced earth
  // core: the same rule helpers/productLabel.js prints "+16" by, so the spec
  // forms and the label can never disagree about whether a cable has one.
  const flagged =
    mixedRaw === true || mixedRaw === 'true' || mixedRaw === 1 || mixedRaw === '1';
  const impliedGy =
    mixedRaw == null &&
    Number.isFinite(Number(gy)) &&
    Number(gy) > 0 &&
    Number(gy) !== Number(section);

  const product = {
    cableName: job?.name || fromKey?.cableName || '',
    numberOfConductors: job?.numberOfConductors ?? fromKey?.numberOfConductors,
    section,
    hasMixedConductorSections: flagged || impliedGy,
    greenYellowSection: gy
  };

  const name = product.cableName;
  const specs = specForms(product);
  // a string on a raw job, a { label, type } object on the project DTO
  const status =
    typeof job?.status === 'string' ? job.status : job?.status?.label;

  const parts = [
    name,
    String(job?.jobId || '').slice(0, 8),
    status,
    // The finished cable's colour lives on the launch key; `job.color` is often
    // unset — see helpers/productColor.js.
    fromKey?.color || job?.color,
    // What the row actually prints ("CYYF 3x25+16"), so pasting from the screen
    // works even for a shape `specForms` does not generate.
    job?.productLabel,
    ...specs,
    ...specs.map(s => `${name} ${s}`)
  ];

  const key = normalise(parts.filter(Boolean).join(' '));
  // A spaceless copy, so "3x25+16" finds a row that spells it "3 x 25 + 16".
  return `${key} ${key.replace(/ /g, '')}`;
}

/**
 * The product a key stands for.
 *
 * `CYYF|3|2.5|0||GREY` is how a product is identified everywhere in the
 * factory, and it carries the whole spec — cores, section, whether the earth is
 * a different size and how big. Parsing it means a launched product can be
 * searched by the same words as an order line, even when no line exists for it
 * yet: looking for "CYYF 3x25+16" should find the drums of it sitting in stock,
 * not just the orders asking for it.
 */
export function productFromKey(key) {
  const parts = String(key || '').split('|');
  if (parts.length < 3) return null;

  const [cableName, cores, section, mixed, gy, color] = parts;
  const n = v => {
    const x = Number(String(v ?? '').replace(',', '.'));
    return Number.isFinite(x) ? x : 0;
  };

  return {
    cableName: cableName || '',
    numberOfConductors: n(cores),
    section: n(section),
    hasMixedConductorSections: mixed === '1' || mixed === 'true',
    greenYellowSection: n(gy) || null,
    color: color || ''
  };
}
